"use client";

import { useEffect, useState } from "react";

export interface MotionPreference {
  /** User has requested reduced motion at the OS level. */
  reducedMotion: boolean;
  /** Viewport is phone-sized (<768px). */
  isMobile: boolean;
  /** Best-effort guess that this device can't comfortably run WebGL/3D. */
  isLowPower: boolean;
  /** Convenience: should we render the full cinematic experience? */
  enableHeavyMotion: boolean;
}

const MOBILE_QUERY = "(max-width: 767px)";
const REDUCED_QUERY = "(prefers-reduced-motion: reduce)";

function guessLowPower(): boolean {
  if (typeof navigator === "undefined") return false;
  const cores = (navigator as Navigator & { hardwareConcurrency?: number }).hardwareConcurrency;
  const memory = (navigator as Navigator & { deviceMemory?: number }).deviceMemory;
  if (typeof cores === "number" && cores > 0 && cores <= 4) return true;
  if (typeof memory === "number" && memory > 0 && memory <= 4) return true;
  return false;
}

/**
 * Single source of truth for whether to play the heavy cinematic motion.
 * SSR-safe: starts conservative (no heavy motion) and upgrades after mount,
 * so the first paint never assumes capabilities we haven't confirmed.
 */
export function useMotionPreference(): MotionPreference {
  const [state, setState] = useState<MotionPreference>({
    reducedMotion: false,
    isMobile: false,
    isLowPower: false,
    enableHeavyMotion: false,
  });

  useEffect(() => {
    const mobileMq = window.matchMedia(MOBILE_QUERY);
    const reducedMq = window.matchMedia(REDUCED_QUERY);
    const lowPower = guessLowPower();

    const update = () => {
      const reducedMotion = reducedMq.matches;
      const isMobile = mobileMq.matches;
      setState({
        reducedMotion,
        isMobile,
        isLowPower: lowPower,
        enableHeavyMotion: !reducedMotion,
      });
    };

    update();
    mobileMq.addEventListener("change", update);
    reducedMq.addEventListener("change", update);
    return () => {
      mobileMq.removeEventListener("change", update);
      reducedMq.removeEventListener("change", update);
    };
  }, []);

  return state;
}
